// frontend/src/utils/structurizrLinter.js

// Import Severity from plantumlLinter and re-export it.
// Values MUST be identical to plantumlLinter.Severity because CodeGenerator
// maps Monaco marker levels using plantumlLinter.Severity constants.
import { Severity } from './plantumlLinter';
export { Severity };

/**
 * Lint Structurizr DSL code.
 * @param {string} code
 * @param {{ allowedCodes?: string[], allowedNames?: string[] }} options
 * @returns {{ line: number, message: string, severity: string, ruleId: string }[]}
 */
export function lintStructurizrDsl(code, { allowedCodes = [], allowedNames = [] } = {}) {
  if (!code || !code.trim()) return [];
  const diags = [];
  const lines = code.split('\n');

  // ── Rule: missing-workspace ──────────────────────────────────────────────
  if (!/^\s*workspace\b/im.test(code)) {
    diags.push({
      ruleId: 'missing-workspace',
      message: 'Отсутствует блок workspace { ... }',
      line: 1,
      severity: Severity.ERROR,
    });
    // If there's no workspace block at all, the rest of the checks are
    // meaningless — return early.
    return diags;
  }

  // ── Rule: missing-model ──────────────────────────────────────────────────
  if (!/\bmodel\s*\{/s.test(code)) {
    const workspaceLine = lines.findIndex(l => /^\s*workspace\b/i.test(l));
    diags.push({
      ruleId: 'missing-model',
      message: 'Внутри workspace отсутствует блок model { ... }',
      line: workspaceLine + 1 || 1,
      severity: Severity.ERROR,
    });
  }

  // ── Rule: unbalanced-braces ──────────────────────────────────────────────
  const open = (code.match(/\{/g) || []).length;
  const close = (code.match(/\}/g) || []).length;
  if (open !== close) {
    diags.push({
      ruleId: 'unbalanced-braces',
      message: `Несбалансированные скобки: ${open} открывающих, ${close} закрывающих`,
      line: lines.length,
      severity: Severity.ERROR,
    });
  }

  // ── Collect declared identifiers from model block ────────────────────────
  // Format: id = person|softwareSystem|container|component ["Name"|CODE] ...
  // Capture: m[1]=identifier, m[2]=dslType, m[3]=quoted name, m[4]=unquoted first word
  const declaredIds = new Set();
  const idToType = new Map();
  const idToFirstArg = new Map(); // id → first argument (quoted name or bare code)
  const idToQuotedArgs = new Map(); // id → all quoted declaration args
  const declRe = /^\s*(\w+)\s*=\s*(person|softwareSystem|container|component)\b\s*(?:"([^"]*)"|([\w]+))?/i;

  lines.forEach((raw) => {
    const m = declRe.exec(raw);
    if (m) {
      declaredIds.add(m[1]);
      idToType.set(m[1], m[2].toLowerCase());
      const firstArg = m[3] !== undefined ? m[3] : (m[4] || null);
      if (firstArg !== null) idToFirstArg.set(m[1], firstArg);
      const quotedArgs = [];
      const quotedRe = /"([^"]*)"/g;
      let quotedMatch;
      while ((quotedMatch = quotedRe.exec(raw)) !== null) quotedArgs.push(quotedMatch[1]);
      idToQuotedArgs.set(m[1], quotedArgs);
    }
  });

  const isDatabaseContainer = (id) => {
    const quotedArgs = idToQuotedArgs.get(id) || [];
    const technology = (quotedArgs[2] || '').toLowerCase();
    if (/postgres|mysql|mongo|redis|oracle|sqlite|dynamo|cassandra|mssql/.test(technology)) {
      return true;
    }

    const declarationIdx = lines.findIndex(l => new RegExp(`\\b${id}\\s*=\\s*container\\b`, 'i').test(l));
    if (declarationIdx === -1) return false;
    const blockSlice = lines.slice(declarationIdx, Math.min(lines.length, declarationIdx + 8)).join('\n').toLowerCase();
    return /\btags\b[\s\S]*"database"/.test(blockSlice);
  };

  // ── Rule: undefined-identifier ───────────────────────────────────────────
  // Relations: id -> id "label"
  const relationRe = /^\s*(\w+)\s*->\s*(\w+)/;
  lines.forEach((raw, idx) => {
    const m = relationRe.exec(raw);
    if (!m) return;
    const from = m[1], to = m[2];
    // Skip if the relation is inside a nested block (e.g. views) — only check
    // if both sides look like user-defined identifiers (not keywords).
    const keywords = new Set(['include', 'exclude', 'autoLayout', 'description', 'properties']);
    if (keywords.has(from) || keywords.has(to)) return;
    if (!declaredIds.has(from)) {
      diags.push({
        ruleId: 'undefined-identifier',
        message: `Неизвестный идентификатор: "${from}" (не объявлен в model)`,
        line: idx + 1,
        severity: Severity.WARNING,
      });
    }
    if (!declaredIds.has(to)) {
      diags.push({
        ruleId: 'undefined-identifier',
        message: `Неизвестный идентификатор: "${to}" (не объявлен в model)`,
        line: idx + 1,
        severity: Severity.WARNING,
      });
    }
  });

  // ── Registry checks ──────────────────────────────────────────────────────
  if (allowedCodes.length > 0) {
    const codesSet = new Set(allowedCodes);
    const namesSet = new Set(allowedNames);
    idToType.forEach((dslType, id) => {
      const firstArg = idToFirstArg.get(id);
      // Recognised if: identifier is a registry code, OR first arg is a registry code (bare),
      // OR first arg is a known display name (inserted via autocomplete with quotes).
      const isKnown = codesSet.has(id)
        || (firstArg && codesSet.has(firstArg))
        || (firstArg && namesSet.size > 0 && namesSet.has(firstArg));
      if (dslType === 'softwaresystem' && !isKnown) {
        const lineIdx = lines.findIndex(l => new RegExp(`\\b${id}\\s*=\\s*softwareSystem\\b`, 'i').test(l));
        diags.push({
          ruleId: 'registry-unknown-softwaresystem',
          message: `Код системы "${id}" не найден в реестре ИС`,
          line: lineIdx + 1 || 1,
          severity: Severity.WARNING,
        });
      }
      if (dslType === 'container' && !isKnown && !isDatabaseContainer(id)) {
        const lineIdx = lines.findIndex(l => new RegExp(`\\b${id}\\s*=\\s*container\\b`, 'i').test(l));
        diags.push({
          ruleId: 'registry-unknown-container',
          message: `Код контейнера "${id}" не найден в реестре ИС`,
          line: lineIdx + 1 || 1,
          severity: Severity.WARNING,
        });
      }
    });
  }

  return diags;
}
